import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/components/View';
import { useState, useCallback, useMemo, useContext, useRef } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { CommentsIcon } from 'foundations-assets/icons/components/CommentsIcon';
import { Text } from 'foundations-components/components/Text';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CommentViewerPopoverContent } from './CommentViewerPopoverContent';
import { CommentsContext } from 'object-comments-thread-lib/contexts/CommentsContext';
import { CommentThread } from 'object-comments-thread-lib/components/comments/CommentComponents/CommentThread';
import { getEarliestComment, isTimestampInRange } from '../../utils/commentUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CommentViewerButton = ({
  utteranceStartTime,
  nextUtteranceStartTime,
  speakerName,
  allComments
}) => {
  const theme = useTheme();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverBodyRef = useRef(null);
  const stopKeyPropagationRef = useRef(event => {
    event.stopPropagation();
  });
  const {
    anchorComments
  } = useContext(CommentsContext);
  const threads = useMemo(() => {
    var _anchorComments$filte;
    return (_anchorComments$filte = anchorComments === null || anchorComments === void 0 ? void 0 : anchorComments.filter(t => {
      var _t$comment$metaDescri;
      return isTimestampInRange((_t$comment$metaDescri = t.comment.metaDescription) === null || _t$comment$metaDescri === void 0 || (_t$comment$metaDescri = _t$comment$metaDescri.options) === null || _t$comment$metaDescri === void 0 ? void 0 : _t$comment$metaDescri.callTimestamp, utteranceStartTime, nextUtteranceStartTime);
    })) !== null && _anchorComments$filte !== void 0 ? _anchorComments$filte : [];
  }, [anchorComments, utteranceStartTime, nextUtteranceStartTime]);
  const timestampComments = useMemo(() => allComments.filter(comment => {
    var _comment$metaDescript;
    return isTimestampInRange((_comment$metaDescript = comment.metaDescription) === null || _comment$metaDescript === void 0 || (_comment$metaDescript = _comment$metaDescript.options) === null || _comment$metaDescript === void 0 ? void 0 : _comment$metaDescript.callTimestamp, utteranceStartTime, nextUtteranceStartTime);
  }), [allComments, utteranceStartTime, nextUtteranceStartTime]);
  const earliestComment = useMemo(() => getEarliestComment(timestampComments), [timestampComments]);
  const commentCount = anchorComments != null ? threads.reduce((sum, t) => {
    var _t$replies$length, _t$replies;
    return sum + 1 + ((_t$replies$length = (_t$replies = t.replies) === null || _t$replies === void 0 ? void 0 : _t$replies.length) !== null && _t$replies$length !== void 0 ? _t$replies$length : 0);
  }, 0) : timestampComments.length;
  const handleToggle = useCallback(() => {
    setIsPopoverOpen(prev => !prev);
  }, []);
  const setPopoverBodyRef = useCallback(element => {
    const previousElement = popoverBodyRef.current;
    if (previousElement) {
      previousElement.removeEventListener('keydown', stopKeyPropagationRef.current);
      previousElement.removeEventListener('keypress', stopKeyPropagationRef.current);
    }
    popoverBodyRef.current = element;
    if (element) {
      element.addEventListener('keydown', stopKeyPropagationRef.current);
      element.addEventListener('keypress', stopKeyPropagationRef.current);
    }
  }, []);
  if (commentCount === 0) {
    return null;
  }
  const popoverBody = /*#__PURE__*/_jsx("div", {
    ref: setPopoverBodyRef,
    style: {
      maxWidth: '280px',
      maxHeight: '400px',
      overflowY: 'auto'
    },
    children: threads.length > 0 ? /*#__PURE__*/_jsx(View, {
      padding: "100",
      children: threads.map(t => /*#__PURE__*/_jsx("div", {
        style: {
          maxWidth: '280px',
          overflow: 'hidden'
        },
        children: /*#__PURE__*/_jsx(CommentThread, {
          commentThread: t,
          variant: "ADVANCED",
          isPaginationEnabled: false
        })
      }, t.comment.id))
    }) : earliestComment ? /*#__PURE__*/_jsx(CommentViewerPopoverContent, {
      comment: earliestComment,
      speakerName: speakerName,
      timestampSeconds: utteranceStartTime,
      totalCommentsAtTimestamp: timestampComments.length
    }) : /*#__PURE__*/_jsx(Text, {
      padding: "400",
      "data-test-id": "commentViewerNoComments",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "recordingReviewComponents.transcript.commentViewer.noComments"
      })
    })
  });
  return /*#__PURE__*/_jsx(Popover, {
    open: isPopoverOpen,
    onOpenChange: evt => setIsPopoverOpen(evt.target.value),
    placement: "bottom",
    closeOnTargetLeave: false,
    content: {
      body: popoverBody
    },
    children: /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative',
        display: 'inline-flex'
      },
      children: [/*#__PURE__*/_jsx(IconButton, {
        use: "transparent",
        size: "md",
        onClick: handleToggle,
        "data-test-id": "utteranceCommentViewerIcon",
        "aria-label": I18n.text('recordingReviewComponents.transcript.utterance.viewCommentsIconAriaLabel'),
        children: /*#__PURE__*/_jsx(CommentsIcon, {
          size: "400",
          color: "fill-brand-default"
        })
      }), /*#__PURE__*/_jsx("div", {
        "data-test-id": "utteranceCommentCountBadge",
        "aria-hidden": "true",
        style: {
          position: 'absolute',
          top: 0,
          right: 0,
          minWidth: '14px',
          height: '14px',
          padding: '0 3px',
          borderRadius: theme.borderRadius['full'],
          backgroundColor: theme.color['fill-accent-light-orange-default'],
          color: theme.color['text-primary-default'],
          fontSize: '9px',
          fontWeight: 600,
          lineHeight: '14px',
          textAlign: 'center',
          pointerEvents: 'none',
          boxSizing: 'border-box'
        },
        children: commentCount
      })]
    })
  });
};